import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  setProjectDetails,
  resetToListView,
  openCreateForm,
  openEditForm,
  openProjectView,
  setActiveStudioView,
} from '../../../../store/screeningSpaceSlice';
import { resetTextractorState } from '../../../../store/textractorSlice';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';

import { TopNav, SideNav } from '../../navigation/ConsoleNavigation';

import GetCookie from '../../../utils/GetCookie';
import authNavigate from '../../../globalComponents/AuthNav';

import ErrorPopup from '../../../utils/popup/ErrorPopup';
import SuccessPopup from '../../../utils/popup/SuccessPopup';
import ProcessingPopup from '../../../utils/popup/ProcessingPopup';

import ScreeningSpaceProjectList from './ScreeningSpaceProjectList';
import ScreeningSpaceProjectOverview from './ScreeningSpaceProjectOverview';
import ScreeningSpaceProjectView from './studio/ScreeningSpaceProjectView'
import ScreeningSpaceFormNavigation from './form/ScreeningSpaceFormNavigation';
import DeleteConfirmationModal from '../../../utils/DeleteConfirmationModal';


const ScreeningSpaceHome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const API_BASE_URL = '/pceapi/slr';

  const headers = useMemo(() => ({
    Authorization: GetCookie('access_token')
  }), []);

  // ═══ Redux-persisted state (survives page navigation) ═══
  const {
    showProjectForm,
    editingProject,
    projectDetails,
    activeStudioView,
  } = useSelector((state) => state.screeningSpace);

  /* ===================== VIEW STATE ===================== */
  // showScreeningView: when true, show ScreeningSpaceProjectView (sidebar + content)
  // when false and projectDetails is set, show ScreeningSpaceProjectOverview (stage cards)
  const [showScreeningView, setShowScreeningView] = useState(false);
  const [isTransitioningToOverview, setIsTransitioningToOverview] = useState(false);
  const [overviewLoading, setOverviewLoading] = useState(false);

  /* ===================== DATA STATE ===================== */
  const [projects, setProjects] = useState([]);
  const [total, setTotal] = useState(0);

  /* ===================== QUERY STATE ===================== */
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('ALL');

  /* ===================== GLOBAL POPUPS (Standard Eavior Pattern) ===================== */
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');

  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');

  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');

  const closeErrorPopup = () => setIsErrorPopupVisible(false);
  const closeSuccessPopup = () => setIsSuccessPopupVisible(false);


  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [editOriginIsOverview, setEditOriginIsOverview] = useState(false);
  const [genbiEntry, setGenbiEntry] = useState(null);
  const [textractorEntry, setTextractorEntry] = useState(null);

  /* ===================== DUPLICATE STATE ===================== */
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [projectToDuplicate, setProjectToDuplicate] = useState(null);
  const [duplicateName, setDuplicateName] = useState('');

  useEffect(() => {
    fetchProjects();
  }, [page, size, search, filterType]);

  useEffect(() => {
    if (projectDetails?.project?.id) {
      fetchProjectDetails(projectDetails.project.id);
    }
  }, []);

  // ── Restore LF view when returning from Discovery Dashboard ──
  useEffect(() => {
    if (location.state?.restoreLFView && projectDetails) {
      setShowScreeningView(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.restoreLFView]);

  // ── Restore overview when returning from create form cancel ──
  useEffect(() => {
    if (location.state?.restoreOverview) {
      setShowScreeningView(false);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.restoreOverview]);

  // ── Open a specific SLR project directly when arriving from the Data
  // Fabric dashboard (clicked a card whose category includes 'SLR') ──
  useEffect(() => {
    if (location.state?.openSlrFabricId) {
      const fabricId = location.state.openSlrFabricId;
      navigate(location.pathname, { replace: true, state: {} });
      handleOpenProject({ id: fabricId });
    }
  }, [location.state?.openSlrFabricId]);

  const fetchProjects = async () => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading SLR projects');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/list`,
        {
          page,
          size,
          search: search || undefined,
          filter_type: filterType
        },
        { headers }
      );

      const data = response.data;

      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === 'failed') {
        setPopupErrorMessage(data.message || 'Failed to retrieve projects');
        setIsErrorPopupVisible(true);
        return;
      }

      setProjects(data.data || []);
      setTotal(data.total || 0);
    } catch (err) {
      setPopupErrorMessage(err.message || 'Failed to retrieve projects');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  const fetchProjectDetails = async (projectId, { silent = false, keepScreeningView = false } = {}) => {
    if (!silent) {
      setIsProcessingPopupVisible(true);
      setProcessingMessage('Loading project details');
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/form/get-project-details`,
        { slr_fabric_id: projectId },
        { headers }
      );

      const data = response.data;

      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === 'failed') {
        setPopupErrorMessage(data.message || 'Failed to load project details');
        setIsErrorPopupVisible(true);
        return;
      }

      // When keepScreeningView=true (Refresh clicked from within the screening
      // view), skip the Redux dispatch and navigation entirely. Dispatching
      // openProjectView changes the fabricId prop received by Screening.jsx,
      // which triggers useEffect([fabricId, mode]) → setIsQCCompleted(false),
      // wiping the "Completed" badge. The screening components already
      // refresh their own data via their individual fetch callbacks.
      if (!keepScreeningView) {
        dispatch(openProjectView(data.data));
        setShowScreeningView(false);
      }
    } catch (err) {
      console.error('Error fetching project details:', err);
      if (!silent) {
        setPopupErrorMessage(err.message || 'Failed to load project details');
        setIsErrorPopupVisible(true);
      }
    } finally {
      if (!silent) {
        setIsProcessingPopupVisible(false);
        setIsTransitioningToOverview(false);
      }
    }
  };

  const handleOpenProject = (project) => {
    if (!project?.id) return;
    dispatch(openProjectView({
      slr_fabric_id: project.id,
      project: { id: project.id, name: project.name || '' }
    }));
    setShowScreeningView(false);
    setOverviewLoading(true);
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading project details...');
    fetchProjectDetails(project.id, { silent: true });
  };

  const handleCreateProject = () => {
    dispatch(openCreateForm());
  };

  const handleEditProject = (project) => {
    // If called from overview without a project arg, use the current projectDetails
    const targetProject = project || (projectDetails ? { id: projectDetails.slr_fabric_id } : null);
    if (!targetProject?.id) return;
    // Track whether edit was launched from the overview so we can return there
    setEditOriginIsOverview(!!projectDetails && !project);
    dispatch(openEditForm(targetProject));
  };

  const handleBackToProjects = () => {
    const projectId = editingProject?.id || projectDetails?.project?.id || projectDetails?.slr_fabric_id;
    const currentProjectDetails = projectDetails;
    const isEditing = !!editingProject;
    setShowScreeningView(false);
    setEditOriginIsOverview(false);
    dispatch(resetTextractorState());

    if (isEditing && projectId) {
      // Dispatch skeleton so overview mounts immediately with fabricId
      dispatch(openProjectView({ slr_fabric_id: projectId, project: currentProjectDetails?.project || {} }));
      setOverviewLoading(true);
      setIsProcessingPopupVisible(true);
      setProcessingMessage('Loading project details...');
      // Fetch fresh data so all stage cards get updated — config_id and stages.*
      // are absent from the skeleton, causing abstractsCount=0 and
      // fetchGenbiTemplates to bail early (configId undefined).
      fetchProjectDetails(projectId, { silent: true });
      return;
    }
    dispatch(resetToListView());
    fetchProjects();
  };

  // From ScreeningSpaceProjectView "Back" → go to overview, not project list.
  const handleBackToOverview = () => {
    dispatch(setActiveStudioView('ABSTRACT_LIST'));
    setGenbiEntry(null);
    setTextractorEntry(null);
    setOverviewLoading(true);
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading project overview...');
    setShowScreeningView(false);
  };

  // Every studio entry point must state which view it wants.
  const handleOpenScreening = () => {
    dispatch(setActiveStudioView('ABSTRACT_LIST'));
    setGenbiEntry(null);
    setTextractorEntry(null);
    setShowScreeningView(true);
  };

  const handleOpenDataExtraction = () => {
    dispatch(setActiveStudioView('DATA_EXTRACTION'));
    setTextractorEntry(null);
    setShowScreeningView(true);
  };

  // Same pattern as handleOpenGenbi: the overview already created/opened the
  // project's sheet; the embedded Textractor workspace opens it from this entry.
  const handleOpenTextractor = (entry) => {
    setTextractorEntry(entry);
    dispatch(setActiveStudioView('DATA_EXTRACTION'));
    setShowScreeningView(true);
  };

  const handleOpenGenbi = (entry) => {
    setGenbiEntry(entry);
    dispatch(setActiveStudioView('REPORTS'));

    // If called from within the form, close the form by setting projectDetails
    // skeleton in Redux (clears showProjectForm). No fetchProjectDetails needed —
    // genbiEntry has all the template data GenBI requires.
    if (showProjectForm && editingProject?.id) {
      dispatch(openProjectView({
        slr_fabric_id: editingProject.id,
        project: { id: editingProject.id, name: editingProject.name || '' }
      }));
    }

    setShowScreeningView(true);
  };

  const handleNavigateToDiscovery = () => {
    navigate('/literature-discovery', { state: { openCreateForm: true, fromSLR: true } });
  };

  const handleOpenDataManager = (slrProjectId) => {
    navigate('/unstructured-threads', {
      state: {
        fromSLR: true,
        slrProjectId: slrProjectId,
      }
    });
  };

  const handleDeleteProject = (project) => {
    if (!project?.id) return;
    setProjectToDelete(project);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteConfirm(false);

    if (!projectToDelete?.id) return;

    setIsProcessingPopupVisible(true);
    setProcessingMessage(`Deleting SLR project "${projectToDelete.name}"`);

    try {
      const response = await axios.delete(
        `${API_BASE_URL}/delete`,
        {
          headers,
          data: { slr_fabric_id: projectToDelete.id }
        }
      );

      const data = response.data;

      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === 'failed') {
        setPopupErrorMessage(data.message || 'Failed to delete SLR project');
        setIsErrorPopupVisible(true);
        return;
      }

      setPopupSuccessMessage(data.message || 'SLR project deleted successfully');
      setIsSuccessPopupVisible(true);

      if (projectDetails?.project?.id === projectToDelete.id) {
        dispatch(setProjectDetails(null));
      }

      fetchProjects();
    } catch (err) {
      console.error('Error deleting SLR project:', err);
      setPopupErrorMessage(err.message || 'Failed to delete SLR project');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
      setProjectToDelete(null);
    }
  };

  const getDeleteModalBodyContent = () => (
    <div>
      <p className="mb-0">This will permanently delete:</p>
      <ul>
        <li>All screening data</li>
        <li>All allocations and decisions</li>
        <li>All configurations for this SLR project</li>
      </ul>
      <p className="text-danger mb-0">This action cannot be undone.</p>
    </div>
  );

  /* ===================== DUPLICATE HANDLERS ===================== */
  const handleDuplicateProject = (project) => {
    if (!project?.id) return;
    setProjectToDuplicate(project);
    setDuplicateName(`Copy of ${project.name}`);
    setShowDuplicateModal(true);
  };

  const handleConfirmDuplicate = async () => {
    if (!projectToDuplicate?.id || !duplicateName.trim()) return;

    setShowDuplicateModal(false);
    setIsProcessingPopupVisible(true);
    setProcessingMessage(`Duplicating project "${projectToDuplicate.name}"...`);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/form/duplicate`,
        { slr_fabric_id: projectToDuplicate.id, new_name: duplicateName.trim() },
        { headers }
      );

      const data = response.data;

      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === 'failed') {
        setPopupErrorMessage(data.message || 'Failed to duplicate project');
        setIsErrorPopupVisible(true);
        return;
      }

      // Open the new project directly in edit mode
      dispatch(openEditForm({ id: data.fabric_id }));

    } catch (err) {
      setPopupErrorMessage(err.message || 'Failed to duplicate project');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
      setProjectToDuplicate(null);
    }
  };

  const handleCloseProjectDetails = () => {
    dispatch(setProjectDetails(null));
    setShowScreeningView(false);
  };

  const handleRefreshProjectDetails = () => {
    if (projectDetails?.slr_fabric_id) {
      // Pass keepScreeningView=true when the user is already inside the screening
      // view — this prevents setShowScreeningView(false) from firing and unmounting
      // Screening / FullTextList (which would reset migrationStatus, isQCCompleted, etc.)
      fetchProjectDetails(projectDetails.slr_fabric_id, { keepScreeningView: showScreeningView });
    }
  };

  const handleOpenPrisma = () => {
    setPopupSuccessMessage('PRISMA will be wired after prisma API is finalized');
    setIsSuccessPopupVisible(true);
  };

  // Determine which view to show:
  // 1. showProjectForm → Form
  // 2. projectDetails + showScreeningView → ScreeningSpaceProjectView (sidebar + content)
  // 3. projectDetails + !showScreeningView → ScreeningSpaceProjectOverview (stage cards)
  // 4. else → Project List
  const isInOverview = projectDetails && !showScreeningView && !showProjectForm;
  const isInScreeningView = projectDetails && showScreeningView && !showProjectForm;

  return (
    <div className="main-window">
      <TopNav />

      <div
        className={`container-fluid${
          isInScreeningView && activeStudioView === 'DATA_EXTRACTION'
            ? ' has-inner-panel has-dashboard-header slr-extraction-shell'
            : isInScreeningView && activeStudioView === 'REPORTS'
            ? ' has-inner-panel slr-genbi-shell'
            : ''
        }`}
      >
        <div className="row">
          <SideNav />

          <main className="col-12 col-md-9 col-lg-10 ms-sm-auto console-main-window">
            <div className="mx-0 pb-3">

              {/* PAGE HEADER — list view only (no project selected, not in form) */}
              {!projectDetails && !showProjectForm && (
                <div className="ev-card-2 dashboard-header position-static mb-2 w-100">
                  <div></div>

                  <p className="gradient-font5 ev-fs-18 ev-bold-med mb-0">
                    Screening Space
                  </p>

                  <button
                    className="ev-btn-yellow-fill-round ev-btn-reg ev-bold-reg avatar-tooltip"
                    data-tooltip="Click on New SLR Project to create"
                    onClick={handleCreateProject}
                  >
                    <i className="bi bi-plus-circle me-1"></i>
                    New SLR Project
                  </button>
                </div>
              )}

              {/* Show form header when in form mode */}
              {showProjectForm && (
                <div className="ev-card-2 dashboard-header position-static mb-3 w-100">
                  <div></div>

                  <p className="gradient-font5 ev-fs-18 ev-bold-med mb-0">
                    Screening Space
                  </p>

                  <div></div>
                </div>
              )}


              {showProjectForm ? (
                <ScreeningSpaceFormNavigation
                  editingProject={editingProject}
                  onBackToProjects={handleBackToProjects}
                  onNavigateToDiscovery={handleNavigateToDiscovery}
                  backLabel={editingProject ? "Back to Project Overview" : "Back to Projects"}
                  onOpenGenbi={handleOpenGenbi}
                />
              ) : isInScreeningView ? (
                <ScreeningSpaceProjectView
                  slrFabricDetails={projectDetails}
                  onBackToProjects={handleBackToOverview}
                  onCreateProject={handleCreateProject}
                  onClose={handleCloseProjectDetails}
                  onRefresh={handleRefreshProjectDetails}
                  onOpenPrisma={handleOpenPrisma}
                  genbiEntry={genbiEntry}
                  textractorEntry={textractorEntry}
                />
              ) : isInOverview ? (
                <ScreeningSpaceProjectOverview
                  slrFabricDetails={projectDetails}
                  onBackToProjects={handleBackToProjects}
                  onCreateProject={handleCreateProject}
                  onEditProject={() => handleEditProject()}
                  onOpenScreening={handleOpenScreening}
                  onOpenDataExtraction={handleOpenDataExtraction}
                  onOpenGenbi={handleOpenGenbi}
                  onOpenTextractor={handleOpenTextractor}
                  onRefresh={handleRefreshProjectDetails}
                  onStatsLoaded={() => {
                    if (overviewLoading) {
                      setOverviewLoading(false);
                      setIsProcessingPopupVisible(false);
                      setProcessingMessage('');
                    }
                  }}
                />
              ) : (
                <ScreeningSpaceProjectList
                  projects={projects}
                  total={total}
                  page={page}
                  setPage={setPage}
                  size={size}
                  setSize={setSize}
                  search={search}
                  setSearch={setSearch}
                  filterType={filterType}
                  setFilterType={setFilterType}
                  onRefresh={fetchProjects}
                  onOpenProject={handleOpenProject}
                  onEditProject={handleEditProject}
                  onDeleteProject={handleDeleteProject}
                  onDuplicateProject={handleDuplicateProject}
                  onOpenDataManager={handleOpenDataManager}
                />
              )}

              <DeleteConfirmationModal
                show={showDeleteConfirm}
                onClose={() => {
                  setShowDeleteConfirm(false);
                  setProjectToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
                objectName="SLR project"
                itemName={projectToDelete?.name}
                bodyContent={getDeleteModalBodyContent()}
              />

              {/* Duplicate Project Modal */}
              {showDuplicateModal && (
                <div className="popup">
                  <div className="popup-content ev-padding-large ev-radius-small">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <i className="bi bi-copy ev-reg-p-blue"></i>
                      <p className="ev-med-p-dark ev-bold-med mb-0">Duplicate Project</p>
                    </div>
                    <p className="ev-reg-p-dark mb-3">
                      A copy of <strong>{projectToDuplicate?.name}</strong> will be created with the
                      same protocol, criteria, tags, and AI model configuration.
                    </p>
                    <div className="mb-3">
                      <label className="ev-small-p-dark mb-1">New Project Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={duplicateName}
                        onChange={(e) => setDuplicateName(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="ev-btn-light-fill ev-btn-reg"
                        onClick={() => { setShowDuplicateModal(false); setProjectToDuplicate(null); }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="ev-btn-blue-fill-round ev-btn-reg"
                        onClick={handleConfirmDuplicate}
                        disabled={!duplicateName.trim()}
                      >
                        <i className="bi bi-copy me-1"></i>
                        Duplicate
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Global Popups (Standard Eavior Pattern) */}
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage || "Processing..."} />
      )}

      {isErrorPopupVisible && (
        <ErrorPopup
          message={popupErrorMessage}
          onClose={closeErrorPopup}
        />
      )}

      {isSuccessPopupVisible && (
        <SuccessPopup
          message={popupSuccessMessage}
          onClose={closeSuccessPopup}
        />
      )}
    </div>
  );
};

export default ScreeningSpaceHome;